import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFetchAccountUsers from '../../../hooks/useApi/useFetchAccountUsers';
import usePostDeleteAccountUser from '../../../hooks/useApi/usePostDeleteAccountUser';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';

import UserList from './UsersList';
import AccountUserPermissionsDrawer from './UserPermissions/AccountUserPermissionsDrawer';
import AddUserDialog, { type AddUserDialogResult } from './AddUserDialog';
import { useDialog, type OpenDialogProps } from '@/providers/Dialog.provider';

type AccountUserType = {
  Username: string;
  'Date Added'?: string;
  UserType: string;
};

type UsersContainerProps = {
  details: vp.AccountDetailsMap;
};

type FC = FunctionComponent<UsersContainerProps>;

const UsersContainer: FC = ({ details }: UsersContainerProps) => {
  const { accountId } = useParams<{ accountId: string }>();
  const [DeleteResponse, deleteAccountUser] = usePostDeleteAccountUser({ bearer: details[accountId].token });
  const [AccountUsers, getAccountUsers] = useFetchAccountUsers({ bearer: details[accountId].token });

  const [isDeleteUser, setIsDeleteUser] = useState(false);
  const [accounts, setAccounts] = useState<AccountUserType[]>([]);
  const [shouldFetchUsers, setFetchUsers] = useState(true);

  const [isPermissionsDrawerOpen, setIsPermissionsDrawerOpen] = useState(false);
  const [permissionsEmail, setPermissionsEmail] = useState('');

  const history = useHistory();
  const dialog = useDialog();
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const { t } = useTranslation('translations', { keyPrefix: 'Users.AccountUsersList.Delete' });
  const { t: tAddUser } = useTranslation('translations', { keyPrefix: 'Users.AddUserDialog' });

  const AddUserDialogForAccount = useMemo(
    () =>
      ({ onConfirm, onCancel }: OpenDialogProps<AddUserDialogResult>) => (
        <AddUserDialog onConfirm={onConfirm} onCancel={onCancel} bearer={details[accountId].token} />
      ),
    [details, accountId],
  );

  useEffect(() => {
    if (!shouldFetchUsers) return;
    getAccountUsers({});
  }, [shouldFetchUsers]);

  useEffect(() => {
    const { Users } = AccountUsers;
    setFetchUsers(false);
    if (Users) {
      const UsersList = Object.values(Users);
      setAccounts(UsersList);
    }
  }, [AccountUsers]);

  useEffect(() => {
    if (!isDeleteUser) return;
    const { Success, ErrorMessage } = DeleteResponse;
    if (!Success && !ErrorMessage) return;

    setIsDeleteUser(false);

    if (Success) {
      alertDialog
        .success(t('successTitle'), t('successSubtitle'), {
          confirmLabel: t('backToAccounts'),
          abortLabel: t('stayHere'),
        })
        .then((result) => {
          if (result === 'confirm') history.push('/accounts');
          else setFetchUsers(true);
        });
    } else {
      alertDialog
        .error(t('errorTitle'), ErrorMessage, {
          confirmLabel: t('backToAccounts'),
          abortLabel: t('stayHere'),
        })
        .then((result) => {
          if (result === 'confirm') history.push('/accounts');
        });
    }
  }, [DeleteResponse]);

  const handlers = {
    onAddUserPage: async () => {
      const result = await dialog.open(AddUserDialogForAccount);
      if (!result) return;
      if (result.status === 'success') {
        toast.success(tAddUser('successToast'));
        setFetchUsers(true);
      } else {
        alertDialog.error(tAddUser('errorTitle'), result.errorMessage);
      }
    },

    onDeleteUser: async (values: vp.DeleteAccountUserRequest) => {
      const result = await alertDialog.caution(t('confirmTitle'), `${values.EmailAddress}\n${t('confirmSubtitle')}`, {
        confirmLabel: t('yes'),
        abortLabel: t('no'),
      });
      if (result !== 'confirm') return;
      setIsDeleteUser(true);
      deleteAccountUser(values);
    },

    onAccountUserPermissionPage: (values: vp.AccountUserPermissionsRequest) => {
      setPermissionsEmail(values.EmailAddress);
      setIsPermissionsDrawerOpen(true);
    },
  };

  return (
    <>
      <UserList handlers={handlers} accounts={accounts} bearer={details[accountId].token} />
      <AccountUserPermissionsDrawer
        isOpen={isPermissionsDrawerOpen}
        onClose={() => setIsPermissionsDrawerOpen(false)}
        emailAddress={permissionsEmail}
        bearer={details[accountId].token}
      />
    </>
  );
};

export default UsersContainer;
